/**
 * 分包记录面板组件 - 与 BudgetOverviewTower 风格一致
 */
import React, { useState, useEffect } from 'react';
import { Spin, message, Tooltip } from 'antd';
import api from '../../../services/api';
import { formatMoney } from '../../../utils/budgetUtils';
import { SubcontractSettlementMethod } from '../../../types';

const fmt = (n: number): string => {
  if (Math.abs(n) >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
};

interface SubcontractPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
  constructionType?: string;
}

interface SubcontractCalculationItem {
  workContent?: string;
  unitPrice?: number;
  measurementUnit?: string;
  actualCompletedQuantity?: number;
  itemTotal?: number;
}

interface SubcontractAdjustmentItem {
  description?: string;
  amount?: number;
}

interface SubcontractRecord {
  _id: string;
  costDate: string;
  contractName: string;
  contractAmount: number;
  paidAmount: number;
  settlementAmount: number;
  settledAmount?: number;
  settlementMethod?: SubcontractSettlementMethod;
  status: 'active' | 'completed' | 'cancelled';
  description?: string;
  subcontractorId?: string;
  subcontractorName?: string;
  constructionType?: string | null;
  calculationItems?: SubcontractCalculationItem[];
  increaseItems?: SubcontractAdjustmentItem[];
  decreaseItems?: SubcontractAdjustmentItem[];
  increaseAmount?: number;
  decreaseAmount?: number;
}

interface SubcontractResponse {
  success: boolean;
  data?: SubcontractRecord[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ============== Tooltip Renderers (from SubcontractCostManagement) ==============
const getCompatibleCalculationItems = (record: SubcontractRecord): SubcontractCalculationItem[] => {
  if (record.calculationItems && record.calculationItems.length > 0) return record.calculationItems;
  if (record.unitPrice !== undefined && record.actualCompletedQuantity !== undefined && record.unitPrice > 0) {
    return [{
      workContent: record.description,
      unitPrice: record.unitPrice,
      measurementUnit: '',
      actualCompletedQuantity: record.actualCompletedQuantity,
      itemTotal: record.contractAmount,
    }];
  }
  return [];
};

const getCompatibleAdjustmentItems = (items: SubcontractAdjustmentItem[] | undefined, amount: number | undefined): SubcontractAdjustmentItem[] => {
  if (items && items.length > 0) return items;
  if (amount && amount > 0) return [{ description: '其他调整', amount }];
  return [];
};

const formatCurrency = (n: number | undefined) => {
  if (n === undefined || n === null) return '—';
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const renderCalculationItemsTooltip = (record: SubcontractRecord) => {
  const items = getCompatibleCalculationItems(record);
  if (items.length === 0) return null;

  const totalAmount = items.reduce((sum, item) => sum + (item.itemTotal || 0), 0);

  return (
    <div style={{ width: 'max-content', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ border: '1px solid #d9e2f2', borderRadius: 8, overflow: 'hidden', background: '#ffffff' }}>
        <div style={{ padding: '8px 12px', background: '#f3f7ff', borderBottom: '1px solid #d9e2f2' }}>
          <span style={{ fontWeight: 600, color: '#1d4ed8' }}>单价包干汇总</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 140px', rowGap: 6, columnGap: 12, padding: '10px 12px' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>明细数量</span>
          <span style={{ fontSize: 12 }}>{items.length} 项</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>应结算金额</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1d4ed8' }}>{formatCurrency(totalAmount)}</span>
        </div>
      </div>
      <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden', background: '#ffffff' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 80px 70px 70px 100px', columnGap: 8, padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
          <span style={{ fontWeight: 600, fontSize: 11, color: '#1d4ed8' }}>施工项目</span>
          <span style={{ fontWeight: 600, fontSize: 11, color: '#1d4ed8', textAlign: 'left' }}>单价</span>
          <span style={{ fontWeight: 600, fontSize: 11, color: '#1d4ed8', textAlign: 'left' }}>单位</span>
          <span style={{ fontWeight: 600, fontSize: 11, color: '#1d4ed8', textAlign: 'left' }}>工程量</span>
          <span style={{ fontWeight: 600, fontSize: 11, color: '#1d4ed8', textAlign: 'left' }}>单项总额</span>
        </div>
        {items.map((item, index) => (
          <div key={index} style={{ display: 'grid', gridTemplateColumns: '180px 80px 70px 70px 100px', columnGap: 8, padding: '8px 12px', borderBottom: index === items.length - 1 ? 'none' : '1px solid #eef2f7' }}>
            <span style={{ fontSize: 11 }}>{item.workContent || '未填写施工项目'}</span>
            <span style={{ fontSize: 11 }}>{formatCurrency(item.unitPrice)}</span>
            <span style={{ fontSize: 11 }}>{item.measurementUnit || '—'}</span>
            <span style={{ fontSize: 11 }}>{item.actualCompletedQuantity ?? '—'}</span>
            <span style={{ fontSize: 11 }}>{formatCurrency(item.itemTotal)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px', borderTop: '1px solid #eef2f7', background: '#f8fafc' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8' }}>合计：{formatCurrency(totalAmount)}</span>
        </div>
      </div>
    </div>
  );
};

const renderAdjustmentSection = (title: string, items: SubcontractAdjustmentItem[], accentColor: string) => {
  if (items.length === 0) {
    return (
      <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden', background: '#ffffff' }}>
        <div style={{ padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
          <span style={{ fontWeight: 600, fontSize: 11, color: accentColor }}>{title}</span>
        </div>
        <div style={{ padding: '10px 12px' }}>
          <span style={{ fontSize: 11, color: '#9ca8b3' }}>暂无明细</span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ border: '1px solid #eef2f7', borderRadius: 8, overflow: 'hidden', background: '#ffffff' }}>
      <div style={{ padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
        <span style={{ fontWeight: 600, fontSize: 11, color: accentColor }}>{title}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', columnGap: 12, padding: '8px 12px' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>说明</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>金额</span>
      </div>
      {items.map((item, index) => (
        <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 100px', columnGap: 12, padding: '8px 12px', borderTop: index === 0 ? 'none' : '1px solid #eef2f7' }}>
          <span style={{ fontSize: 11 }}>{item.description || '未填写说明'}</span>
          <span style={{ fontSize: 11, textAlign: 'right', fontFamily: '"JetBrains Mono", monospace' }}>{formatCurrency(item.amount)}</span>
        </div>
      ))}
    </div>
  );
};

const renderTotalPackageTooltip = (record: SubcontractRecord) => {
  const increaseItems = getCompatibleAdjustmentItems(record.increaseItems, record.increaseAmount);
  const decreaseItems = getCompatibleAdjustmentItems(record.decreaseItems, record.decreaseAmount);
  const increaseTotal = increaseItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const decreaseTotal = decreaseItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  return (
    <div style={{ width: 'max-content', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ border: '1px solid #d9e2f2', borderRadius: 8, overflow: 'hidden', background: '#ffffff' }}>
        <div style={{ padding: '8px 12px', background: '#f3f7ff', borderBottom: '1px solid #d9e2f2' }}>
          <span style={{ fontWeight: 600, color: '#1d4ed8' }}>总价包干汇总</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 140px', rowGap: 6, columnGap: 12, padding: '10px 12px' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>合同金额</span>
          <span style={{ fontSize: 12 }}>{formatCurrency(record.contractAmount)}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>增项合计</span>
          <span style={{ fontSize: 12, color: '#15803d' }}>{formatCurrency(increaseTotal)}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>减项合计</span>
          <span style={{ fontSize: 12, color: '#b91c1c' }}>{formatCurrency(decreaseTotal)}</span>
          <span style={{ fontSize: 12, fontWeight: 600 }}>应结算金额</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#1d4ed8' }}>{formatCurrency(record.settlementAmount)}</span>
        </div>
      </div>
      {renderAdjustmentSection('增项明细', increaseItems, '#15803d')}
      {renderAdjustmentSection('减项明细', decreaseItems, '#b91c1c')}
    </div>
  );
};

const getStatusTag = (status: SubcontractRecord['status']) => {
  const configs = {
    completed: { label: '已完成', color: '#5d8a7f', bg: 'rgba(101,214,184,0.12)' },
    cancelled: { label: '已取消', color: '#c46d5f', bg: 'rgba(196,109,95,0.12)' },
    active: { label: '进行中', color: '#547590', bg: 'rgba(84,117,144,0.1)' },
  };
  const c = configs[status] || configs.active;
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.color}30`,
    }}>
      {c.label}
    </span>
  );
};

const SubcontractPanel: React.FC<SubcontractPanelProps> = ({
  projectId,
  isMobile = false,
  active = false,
  constructionType,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SubcontractRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const fetchData = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await api.get('/subcontract-costs', {
        params: {
          projectId,
          constructionType,
          page: 1,
          limit: 500
        }
      }) as Promise<SubcontractResponse>;

      if (response.success && response.data) {
        setData(response.data);
        setTotal(response.pagination?.total || response.data.length);
      }
    } catch (error) {
      console.error('获取分包记录失败:', error);
      message.error('获取分包记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId, constructionType]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const renderTooltipContent = (record: SubcontractRecord) => {
    if (record.settlementMethod === SubcontractSettlementMethod.UNIT_PRICE_PACKAGE) {
      return renderCalculationItemsTooltip(record);
    }
    return renderTotalPackageTooltip(record);
  };

  const hasTooltipContent = (record: SubcontractRecord) => {
    if (record.settlementMethod === SubcontractSettlementMethod.UNIT_PRICE_PACKAGE) {
      const items = getCompatibleCalculationItems(record);
      return items.length > 0 || record.unitPrice > 0 || record.actualCompletedQuantity > 0 || record.contractAmount > 0;
    }
    const increaseItems = getCompatibleAdjustmentItems(record.increaseItems, record.increaseAmount);
    const decreaseItems = getCompatibleAdjustmentItems(record.decreaseItems, record.decreaseAmount);
    return increaseItems.length > 0 || decreaseItems.length > 0 || record.contractAmount > 0 || record.settlementAmount > 0;
  };

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 0',
        color: 'var(--text-muted)', fontSize: 13,
      }}>
        <Spin size="small" /> 加载中...
      </div>
    );
  }

  if (!data.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 16px',
        color: 'var(--text-muted)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>{'📋'}</div>
        <div style={{ fontSize: 13 }}>暂无分包记录</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ borderRadius: '0 0 var(--radius-sm) var(--radius-sm)', overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
        {/* 表头 */}
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr 90px 80px 80px 80px',
            gap: 8,
            padding: '6px 12px',
            background: 'rgba(0,0,0,0.03)',
            borderBottom: '1px solid var(--border)',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>日期</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>合同名称</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>施工类型</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>应结算</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>已结算</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>已支付</span>
          </div>
        )}
        {data.map((record, i) => (
          <div
            key={record._id}
            className="subcontract-panel-row"
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr auto' : '88px 108px 68px 80px 80px 80px',
              gap: isMobile ? 0 : 8,
              padding: isMobile ? '10px 12px' : '8px 12px',
              borderBottom: i < data.length - 1 ? '1px solid var(--border)' : 'none',
              alignItems: 'center',
              transition: 'background 0.15s',
              cursor: 'default',
            }}
          >
            {isMobile ? (
              <>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>
                    {record.contractName}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                    {formatDate(record.costDate)} · {getStatusTag(record.status)}
                  </div>
                  {record.description && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic' }}>
                      {record.description}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 13, fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-primary)', textAlign: 'right', minWidth: 80 }}>
                  {fmt(record.paidAmount > 0 ? record.paidAmount : record.contractAmount)}
                </div>
              </>
            ) : (
              <>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'nowrap' }}>
                  {formatDate(record.costDate)}
                </span>
                {hasTooltipContent(record) ? (
                  <Tooltip
                    title={renderTooltipContent(record)}
                    placement="topLeft"
                    autoAdjustOverflow
                    overlayStyle={{ maxWidth: 600 }}
                    overlayInnerStyle={{ maxWidth: 600, background: 'rgba(0, 0, 0, 0.8)', opacity: 0.8 }}
                  >
                    <div style={{ cursor: 'help', minWidth: 0, overflow: 'hidden' }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {record.contractName}
                      </div>
                      {record.subcontractorName && (
                        <div style={{ fontSize: 10, color: 'var(--accent-purple)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {record.subcontractorName}
                        </div>
                      )}
                      {record.description && (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {record.description}
                        </div>
                      )}
                    </div>
                  </Tooltip>
                ) : (
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {record.contractName}
                    </div>
                    {record.subcontractorName && (
                      <div style={{ fontSize: 10, color: 'var(--accent-purple)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {record.subcontractorName}
                      </div>
                    )}
                    {record.description && (
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {record.description}
                      </div>
                    )}
                  </div>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {record.constructionType || '—'}
                </span>
                <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', textAlign: 'right', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                  {fmt(record.settlementAmount)}
                </span>
                <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', textAlign: 'right', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {record.settledAmount ? fmt(record.settledAmount) : '—'}
                </span>
                <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', textAlign: 'right', color: 'var(--text-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {fmt(record.paidAmount)}
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      {total > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          padding: '8px 12px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-surface)',
        }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            共 {total} 条，已显示 {data.length} 条
          </span>
        </div>
      )}
    </div>
  );
};

export default SubcontractPanel;
